// Plays an ascending "Glass-like" chime via the Web Audio API — A5 → C6 → E6
// with soft envelopes. Used when a long-running task (e.g. template
// generation) completes so the user doesn't have to keep the tab focused.

type AudioCtxClass = typeof AudioContext;

function getAudioCtxClass(): AudioCtxClass | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as {
    AudioContext?: AudioCtxClass;
    webkitAudioContext?: AudioCtxClass;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

let sharedCtx: AudioContext | null = null;

function emitTone(ctx: AudioContext, freqHz: number, startSec: number, durationSec: number, peakGain = 0.14): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freqHz;
  // Linear ramp up to peak (avoids click), then exponential decay to near-zero
  // for a smooth "glass" tail.
  gain.gain.setValueAtTime(0, startSec);
  gain.gain.linearRampToValueAtTime(peakGain, startSec + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, startSec + durationSec);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startSec);
  osc.stop(startSec + durationSec);
}

/**
 * Create + unlock the shared AudioContext. MUST be called from a user-gesture
 * handler (button click, keypress, etc.) — that's the only time browsers
 * allow audio to start. Subsequent calls are cheap no-ops.
 */
export function warmUpCompletionAudio(): void {
  if (sharedCtx) {
    if (sharedCtx.state === "suspended") {
      void sharedCtx.resume().catch(() => undefined);
    }
    return;
  }
  const AudioCtxClass = getAudioCtxClass();
  if (!AudioCtxClass) return;
  try {
    sharedCtx = new AudioCtxClass();
    if (sharedCtx.state === "suspended") {
      void sharedCtx.resume().catch(() => undefined);
    }
  } catch {
    sharedCtx = null;
  }
}

export function playCompletionSound(): void {
  if (!sharedCtx) warmUpCompletionAudio();
  const ctx = sharedCtx;
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => undefined);
    }
    const t0 = ctx.currentTime;
    // Ascending: A5 → C6 → E6
    emitTone(ctx, 880, t0, 0.3);
    emitTone(ctx, 1046.5, t0 + 0.15, 0.35);
    emitTone(ctx, 1318.5, t0 + 0.3, 0.4);
  } catch {
    // Some browser blocked us — fail silently.
  }
}
